import { useMemo } from 'react';
import { ChevronDown, ChevronRight, Loader2, Minus } from 'lucide-react';
import type { WarehouseTreeNode } from '@/lib/types';
import { buildDescendantWarehouseIds } from '@/lib/utils/warehouse-tree';
import { useTreeView } from '@/hooks/useTreeView';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface WarehouseMultiSelectProps {
  /** 倉庫ツリー（プレオーダー平坦リスト。配列順=表示順） */
  nodes: WarehouseTreeNode[];
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
  /** チェックボックス id を一意にするための接頭辞（同一画面に複数配置するため） */
  idPrefix: string;
  /** ツリー読み込み中（「倉庫がありません」との誤表示を防ぐ） */
  loading?: boolean;
}

/** 1階層ぶんのインデント幅(px) */
const INDENT_PX = 20;

/** チェックボックスをプロダクトの倉庫ツリーに合わせて角型にする（テーマの radius で丸くなるのを打ち消す） */
const SQUARE_CHECKBOX_CLASS = 'rounded-[2px]';

/**
 * 倉庫の複数選択コンポーネント（店舗グループ階層→倉庫のツリー表示。プロダクトの倉庫ツリーと同じ並び順）
 * 選択値は末端の倉庫IDのみ。店舗グループのチェックで配下の倉庫を一括選択/解除できる。
 * 検索（倉庫名・グループ名）と、（検索結果に対する）一括選択・一括解除、選択数表示を持つ。
 *
 * 集計する店舗グループを選ぶツリーは `StoreGroupExclusiveSelect`（選択値がグループIDで排他）。
 */
export function WarehouseMultiSelect({
  nodes,
  selectedIds,
  onChange,
  idPrefix,
  loading = false,
}: WarehouseMultiSelectProps) {
  // 表示制御（検索・折りたたみ）は他のツリーと共通のフックに委ねる
  const viewNodes = useMemo(
    () =>
      nodes.map((n) => ({ name: n.name, level: n.level, isGroup: n.node_type === 'seller_group' })),
    [nodes],
  );
  const {
    search,
    setSearch,
    visibleIndexes,
    hiddenByCollapse,
    collapsedGroups,
    toggleCollapse,
    hasVisibleNodes,
  } = useTreeView(viewNodes);

  const warehouseCount = useMemo(
    () => nodes.filter((n) => n.node_type === 'warehouse').length,
    [nodes],
  );

  // 各グループノード(index)→配下の倉庫IDリスト
  const descendantWarehouseIds = useMemo(() => buildDescendantWarehouseIds(nodes), [nodes]);

  // 一括操作・グループチェックの対象になる倉庫ID集合（検索なしなら全倉庫）
  const visibleWarehouseIdSet = useMemo(() => {
    const set = new Set<string>();
    nodes.forEach((node, index) => {
      if (node.node_type !== 'warehouse') return;
      if (visibleIndexes !== null && !visibleIndexes.has(index)) return;
      set.add(node.id);
    });
    return set;
  }, [nodes, visibleIndexes]);

  const toggle = (id: string, checked: boolean) => {
    const next = new Set(selectedIds);
    if (checked) {
      next.add(id);
    } else {
      next.delete(id);
    }
    onChange(next);
  };

  // グループの一括トグル: 表示中の配下倉庫が全選択済みなら解除、そうでなければ選択（プロダクトの挙動に合わせる）
  const toggleGroup = (index: number) => {
    const targets = (descendantWarehouseIds.get(index) ?? []).filter((id) =>
      visibleWarehouseIdSet.has(id),
    );
    if (targets.length === 0) return;
    const allSelected = targets.every((id) => selectedIds.has(id));
    const next = new Set(selectedIds);
    targets.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
    onChange(next);
  };

  // グループのチェック表示状態（表示中の配下倉庫に対して all=checked / some=indeterminate）
  const groupCheckState = (index: number): boolean | 'indeterminate' => {
    const targets = (descendantWarehouseIds.get(index) ?? []).filter((id) =>
      visibleWarehouseIdSet.has(id),
    );
    if (targets.length === 0) return false;
    const selectedCount = targets.filter((id) => selectedIds.has(id)).length;
    if (selectedCount === 0) return false;
    if (selectedCount === targets.length) return true;
    return 'indeterminate';
  };

  // 一括操作は検索で絞り込まれた対象に対して行う（検索なしなら全倉庫が対象）
  const checkAllFiltered = () => {
    const next = new Set(selectedIds);
    visibleWarehouseIdSet.forEach((id) => next.add(id));
    onChange(next);
  };
  const uncheckAllFiltered = () => {
    const next = new Set(selectedIds);
    visibleWarehouseIdSet.forEach((id) => next.delete(id));
    onChange(next);
  };

  if (loading) {
    return (
      <div className="flex items-center rounded-md border p-3 text-sm text-gray-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        読み込み中...
      </div>
    );
  }

  if (nodes.length === 0) {
    return <div className="rounded-md border p-3 text-sm text-gray-500">倉庫がありません</div>;
  }

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <Input
          placeholder="倉庫名・グループ名で検索"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8"
        />
        <Button type="button" variant="outline" size="sm" onClick={checkAllFiltered}>
          すべて選択
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={uncheckAllFiltered}>
          すべて解除
        </Button>
      </div>
      <div className="text-xs text-gray-500">
        対象倉庫 {selectedIds.size} / {warehouseCount}
      </div>
      <div className="max-h-56 space-y-1 overflow-y-auto">
        {!hasVisibleNodes ? (
          <div className="text-sm text-gray-500">該当する倉庫がありません</div>
        ) : (
          nodes.map((node, index) => {
            if (visibleIndexes !== null && !visibleIndexes.has(index)) return null;
            // 検索中は折りたたみを無視して一致結果を表示する（自動展開扱い）
            if (visibleIndexes === null && hiddenByCollapse.has(index)) return null;
            const indent = { paddingLeft: `${(node.level - 1) * INDENT_PX}px` };
            if (node.node_type === 'seller_group') {
              const collapsed = collapsedGroups.has(index);
              return (
                <div
                  key={`sg-${node.id}-${index}`}
                  className="flex items-center gap-2"
                  style={indent}
                >
                  <button
                    type="button"
                    onClick={() => toggleCollapse(index)}
                    className="flex h-4 w-4 shrink-0 items-center justify-center text-gray-500 hover:text-gray-800"
                    aria-label={collapsed ? '展開' : '折りたたみ'}
                  >
                    {collapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  <Checkbox
                    id={`${idPrefix}-sg-${index}`}
                    className={SQUARE_CHECKBOX_CLASS}
                    checked={groupCheckState(index)}
                    onCheckedChange={() => toggleGroup(index)}
                  />
                  <Label htmlFor={`${idPrefix}-sg-${index}`} className="font-medium text-gray-700">
                    {node.name}
                  </Label>
                </div>
              );
            }
            return (
              <div key={`wh-${node.id}`} className="flex items-center gap-2" style={indent}>
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-gray-300">
                  <Minus className="h-3 w-3" />
                </span>
                <Checkbox
                  id={`${idPrefix}-${node.id}`}
                  className={SQUARE_CHECKBOX_CLASS}
                  checked={selectedIds.has(node.id)}
                  onCheckedChange={(checked) => toggle(node.id, checked === true)}
                />
                <Label htmlFor={`${idPrefix}-${node.id}`} className="font-normal">
                  {node.name}
                </Label>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
